
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Gerador } from "@/types";
import { GeradorService, ClienteService } from "@/services/StorageService";

// Import the refactored components
import { clienteEnergiaFormSchema, ClienteEnergiaFormValues } from "./form/energiaTypes";
import GeradorSelector from "./form/GeradorSelector";
import ClienteInfoFields from "./form/ClienteInfoFields";
import AnaliseViabilidade from "./form/AnaliseViabilidade";
import ConfirmDialog from "./form/ConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";

interface ClienteEnergiaFormProps {
  geradorIdPreSelecionado?: string;
}

const ClienteEnergiaForm = ({ geradorIdPreSelecionado }: ClienteEnergiaFormProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geradores, setGeradores] = useState<Gerador[]>([]);
  const [geradorSelecionado, setGeradorSelecionado] = useState<Gerador | null>(null);
  const [usarDadosGerador, setUsarDadosGerador] = useState(false);
  const [creditosInsuficientes, setCreditosInsuficientes] = useState(false);
  
  const form = useForm<ClienteEnergiaFormValues>({
    resolver: zodResolver(clienteEnergiaFormSchema),
    defaultValues: {
      geradorId: geradorIdPreSelecionado || "",
      nome: "",
      documento: "",
      cep: "",
      logradouro: "",
      bairro: "",
      cidade: "",
      estado: "",
      concessionaria: "",
      numeroCliente: "",
      numeroInstalacao: "",
      gastoEnergia: 0,
      tarifaEnergia: 0
    }
  });
  
  useEffect(() => {
    const todosGeradores = GeradorService.getAll();
    setGeradores(todosGeradores);
    
    if (geradorIdPreSelecionado) {
      const gerador = todosGeradores.find(g => g.id === geradorIdPreSelecionado);
      if (gerador) {
        setGeradorSelecionado(gerador);
        form.setValue("geradorId", gerador.id);
        form.setValue("concessionaria", gerador.concessionaria);
      }
    }
  }, [geradorIdPreSelecionado, form]);
  
  // Calculated values based on form inputs
  const consumoMedio = form.watch("gastoEnergia") / form.watch("tarifaEnergia") || 0;
  
  const economiaEstimada = geradorSelecionado 
    ? consumoMedio * (form.watch("tarifaEnergia") - geradorSelecionado.tarifaVenda) 
    : 0;
  
  const percentualEconomia = form.watch("gastoEnergia") > 0 
    ? (economiaEstimada / form.watch("gastoEnergia")) * 100 
    : 0;
    
  // Calcular o percentual da energia do gerador que está sendo adquirida
  const percentualEnergiaAdquirida = geradorSelecionado && geradorSelecionado.geracaoEstimada > 0
    ? (consumoMedio / geradorSelecionado.geracaoEstimada) * 100
    : 0;
  
  useEffect(() => {
    if (geradorSelecionado && consumoMedio > 0) {
      setCreditosInsuficientes(consumoMedio > geradorSelecionado.creditosDisponiveis);
    } else {
      setCreditosInsuficientes(false);
    }
  }, [consumoMedio, geradorSelecionado]);

  const onSubmit = async (values: ClienteEnergiaFormValues) => {
    setIsSubmitting(true);
    try {
      const clienteData = {
        tipo: 'energia' as const,
        geradorId: values.geradorId,
        nome: values.nome,
        documento: values.documento,
        cep: values.cep,
        logradouro: values.logradouro,
        bairro: values.bairro,
        cidade: values.cidade,
        estado: values.estado,
        concessionaria: values.concessionaria,
        numeroCliente: values.numeroCliente,
        numeroInstalacao: values.numeroInstalacao,
        gastoEnergia: values.gastoEnergia,
        tarifaEnergia: values.tarifaEnergia,
        percentualEnergiaAdquirida: percentualEnergiaAdquirida,
        criadoPor: user?.id
      };
      
      const cliente = ClienteService.create(clienteData);
      
      if (!cliente) {
        toast.error("Erro ao cadastrar cliente. Verifique se há créditos suficientes disponíveis.");
        return;
      }
      
      toast.success("Cliente de energia cadastrado com sucesso!");
      navigate(`/clientes/${cliente.id}`);
    } catch (error) {
      console.error("Erro ao cadastrar cliente:", error);
      toast.error("Erro ao cadastrar cliente");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCepFound = (address: any) => {
    form.setValue("cep", address.cep);
    form.setValue("logradouro", address.logradouro);
    form.setValue("bairro", address.bairro);
    form.setValue("cidade", address.localidade);
    form.setValue("estado", address.uf);
  };

  return (
    <Card className="w-full max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle className="text-energy-blue-dark text-lg">Cadastro de Cliente/Comprador de Energia</CardTitle>
        <CardDescription className="text-sm">
          Preencha os dados do cliente interessado em comprar energia renovável.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Gerador Selector Component */}
            <GeradorSelector 
              form={form} 
              geradores={geradores} 
              geradorSelecionado={geradorSelecionado}
              setGeradorSelecionado={setGeradorSelecionado}
              usarDadosGerador={usarDadosGerador}
              setUsarDadosGerador={setUsarDadosGerador}
            />

            {/* Cliente Info Fields Component */}
            <ClienteInfoFields 
              form={form} 
              usarDadosGerador={usarDadosGerador} 
              handleCepFound={handleCepFound} 
            />

            {/* Análise de Viabilidade Component */}
            <AnaliseViabilidade 
              geradorSelecionado={geradorSelecionado}
              consumoMedio={consumoMedio}
              gastoEnergia={form.watch("gastoEnergia")}
              economiaEstimada={economiaEstimada}
              percentualEconomia={percentualEconomia}
              percentualEnergiaAdquirida={percentualEnergiaAdquirida}
              creditosInsuficientes={creditosInsuficientes}
            />

            {/* Confirm Dialog Component */}
            <ConfirmDialog 
              isSubmitting={isSubmitting} 
              creditosInsuficientes={creditosInsuficientes}
              onConfirm={form.handleSubmit(onSubmit)}
              actionLabel="Salvar Cliente de Energia"
            />
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default ClienteEnergiaForm;
